import {
    useQuery,
    type UseQueryResult,
} from "@tanstack/react-query";
import {useMediaQuery} from "@mantine/hooks";
import {useCallback, useEffect, useMemo, useRef, useState, type Dispatch, type ReactNode, type SetStateAction} from "react";
import {OttopilotColumnType, type OttopilotColumn} from "~/components/ottopilotDataViewer/column";
import type {KanbanColumnConfig, TableColumnConfig} from "~/components/ottopilotDataViewer/columnConfiguration";
import type {VisibleFiltersConfiguration} from "~/components/ottopilotDataViewer/filterComponentStupider";
import type {FilterConfiguration} from "~/components/ottopilotDataViewer/filterConfiguration";
import {RowEngagementPanelType, RowEngagementType, type CurrentlyOpenRowEngagementPanel, type ReadPanelLayout} from "~/components/ottopilotDataViewer/rowEngagementPanel";
import type {SearchConfiguration} from "~/components/ottopilotDataViewer/searchConfiguration";
import type {SortConfiguration} from "~/components/ottopilotDataViewer/sortConfiguration";
import {sortOttopilotViewsForDisplayOrder, ViewType, type OttopilotView} from "~/components/ottopilotDataViewer/view";
import type {Integer, Iso8601DateTime, Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {sanitizeCreatedUpdatedByFilterColumnValues} from "~/utilities/deprecatedSystemGeneratedCreatedUpdatedByUserIds";
import {formatDateTime} from "~/utilities/utilities";
import type {DataViewerFilterClearConfig} from "~/components/ottopilotDataViewer/dataViewerFilterClearConfig";
import {
    DATA_VIEWER_CREATED_AT_DATE_RANGE_COLUMN_ID,
    mergeDefaultCreatedAtMonthFilter,
    readCreatedAtDateRangeFromFilters,
    stripCreatedAtDateRangeLineItems,
    stripDateRangeFilterLineItemsFromFilterConfiguration,
} from "~/components/ottopilotDataViewer/dataViewerDateRangeDefaults";
import {
    buildDataViewerFilterPersistenceStorageKey,
    clearDataViewerFilterPersistence,
    loadDataViewerFilterPersistence,
    resolveFilterConfigurationFromPersistence,
    resolveSearchConfigurationFromPersistence,
    resolveSortConfigurationFromPersistence,
    resolveVisibleFiltersFromPersistence,
    saveDataViewerFilterPersistence,
    stripManagedFilterLineItemsForPersistence,
    type DataViewerFilterPersistenceV1,
} from "~/components/ottopilotDataViewer/dataViewerFilterPersistence";
import {
    buildConfigPageViewPreferencesFromState,
    buildConfigPageViewPreferencesStorageKey,
    clearConfigPageViewPreferences,
    loadConfigPageViewPreferences,
    saveConfigPageViewPreferences,
    toKanbanColumnConfigFromPreferences,
    toTableColumnConfigFromPreferences,
    type ConfigPageViewPreferencesV2,
} from "~/components/ottopilotDataViewer/dataViewerViewPreferences";
import {resolveDataViewerDefaultSortConfiguration} from "~/components/ottopilotDataViewer/dataViewerSortDefaults";
import {
    getJourneyScopeSelectionFromFilter,
    type JourneyScopeFilterConfig,
} from "~/components/ottopilotDataViewer/journeyScopeFilterConfiguration";
import type {ScopeFilterConfig} from "@vesta/vos-types/modules/workspaceLevel/domain/OttopilotDataViewerConfig";
import type {TableRowActionsConfig} from "~/components/table/tableRowActions.types";
import {
    buildCalendarDisplayMetricPersistenceStorageKey,
    loadCalendarDisplayMetricPersistence,
    saveCalendarDisplayMetricPersistence,
} from "~/components/ottopilotDataViewer/calendarDisplayMetricPersistence";
import {resolveCalendarMetricIsoStart} from "~/domainModels/calendarDisplayMetrics/resolveCalendarMetricDate";
import {
    findCalendarDisplayMetricOption,
    resolveCalendarDisplayMetricFilterColumnId,
    resolveDefaultCalendarDisplayMetricId,
    type CalendarDisplayMetricOption,
    type CalendarDisplayMetricsConfig,
    type CalendarDisplayMetricVisibilityContext,
} from "~/domainModels/calendarDisplayMetrics/types";
import type {CalendarOrchestrationEligibility} from "~/utilities/apis/eventContainerAndEventOrchestrationApi";

export type CalendarDataSource = "direct" | "orchestration";

export type OttopilotRowsQueryFn<T extends {id: Uuid}> = (props: {
    filterConfiguration: FilterConfiguration;
    limit: number;
    offset: number;
    sortConfiguration: SortConfiguration;
    searchConfiguration: SearchConfiguration;
    columns?: Array<string> | null;
    maxRowsPerDay?: number | null;
    maxRowsPerDayColumn?: string | null;
}) => Promise<Array<T>>;

/** Matches `useCheckMobile` (`innerWidth < 768`). */
const MOBILE_VIEWPORT_MEDIA_QUERY = "(max-width: 767px)";

export type OttopilotDataViewerController<
    T extends {
        id: Uuid;
    },
> = {
    breadCrumbItems?: {
        displayName: string;
        to: string | null;
    }[];
    // Configuration
    rowDisplayNameVariants: {
        singularSentenceCase: string;
        pluralSentenceCase: string;
        singularLowerCase: string;
        pluralLowerCase: string;
    };
    columns: {
        revisionId: Uuid;
        lineItems: Array<OttopilotColumn<T>>;
    };
    views: Array<OttopilotView<T>>;
    queryKeyPrefix: Array<string | null>;
    hideEditPanelFooter?: boolean;
    /** When true, hides the Edit item from the read (view) panel header ⋯ menu. */
    hideReadPanelEditMenuItem?: boolean;
    /** When true, Edit opens the update panel in fullscreen instead of sidebar. */
    updatePanelOpensInFullscreen?: boolean;
    /** When false, hides expand/shrink controls that toggle sidebar vs fullscreen panel layout. */
    rowEngagementPanelAllowsFullscreenToggle?: boolean;
    /** When true, mobile row-engagement panels stay within the page content between the app header and bottom navigation. */
    mobileRowEngagementPanelInContentArea?: boolean;
    /**
     * When true (default), mobile viewports default to list view when a list view exists.
     * Users can still switch views via the view picker. Set false for My Day Plan / Home
     * so Focus / Kanban / explicit deep links can be the initial mobile view.
     */
    forceListViewOnMobile?: boolean;
    /** When true, evaluate edit action visibility per row instead of the first row. */
    showEditActionPerRow?: boolean;
    rowsQueryFn: OttopilotRowsQueryFn<T>;
    /** Calendar-only row transport; defaults to rowsQueryFn for direct/legacy calendars. */
    calendarRowsQueryFn: OttopilotRowsQueryFn<T>;
    /**
     * Year calendar: day-bucketed counts (`count-by-column` with `bucket: "day"`).
     * When non-null, Year view does not load lead rows.
     */
    calendarYearDayCountsQueryFn: ((props: {
        filterConfiguration: FilterConfiguration;
        searchConfiguration: SearchConfiguration;
        dateColumnId: string;
    }) => Promise<Array<{count: number; value: string | null}>>) | null;
    /** Columns projected for Month/Week/Day calendar chip rendering (id, date, status, title fields). */
    calendarRowColumns: Array<string> | null;
    rowCountQueryFn: (props: {
        filterConfiguration: FilterConfiguration;
        searchConfiguration: SearchConfiguration;
    }) => Promise<Integer>;
    /** Optional. When set, kanban column headers use these totals instead of loaded row count. */
    kanbanColumnRowCountsQueryFn?: (props: {
        filterConfiguration: FilterConfiguration;
        sortConfiguration: SortConfiguration;
        searchConfiguration: SearchConfiguration;
    }) => Promise<Record<string, number>>;
    /**
     * When true, kanban column infinite scroll advances API offset by the requested page size
     * (not the client-filtered page length) and uses row counts to stop.
     */
    kanbanAdvanceOffsetByPageSize?: boolean;
    dateRangeFieldIds?: {
        startDateColumnId?: string;
        endDateColumnId?: string;
    };
    /** Date-picker column, independent of calendar display dates. Defaults to createdAt. */
    dateRangeColumnId?: string;
    pageSize: number;
    setPageSize: Dispatch<SetStateAction<number>>;
    /** 1-based page index for table/list paginated views. */
    pageNumber: number;
    setPageNumber: Dispatch<SetStateAction<number>>;
    // filterConfiguration: FilterConfiguration
    // TODO: Narrow down values based on filterConfiguration?
    columnValuesQueryFn: (props: {
        columnId: string;
        filterConfiguration?: FilterConfiguration;
        searchConfiguration?: SearchConfiguration;
    }) => Promise<Array<string>>;
    createPanelComponent?: (isExpanded: boolean) => React.ReactNode;
    // deletePanelComponent: (row: T) => React.ReactNode;
    readPanelComponent?: (row: T, isExpanded: boolean) => React.ReactNode;
    /** Optional footer for the read (view) panel; replaces the default Edit footer button. */
    readPanelFooterComponent?: (row: T) => React.ReactNode;
    /** Optional title for the read panel header; defaults to "View {singularLowerCase}". */
    resolveReadPanelHeaderLeft?: (props: {row: T; readPanelLayout?: ReadPanelLayout}) => string | undefined;
    updatePanelComponent?: (row: T, isExpanded: boolean) => React.ReactNode;
    additionalActionComponent?: (row: T) => React.ReactNode;
    tableRowActions?: TableRowActionsConfig<T>;
    rowEngagementDropdownOptions?: (row: T) => Array<React.ReactNode>;
    tableSelectionBulkAction?: "delete" | "none" | "reinvite" | "restore";
    /** When false, hides row checkboxes in table and desktop list views. Default true. */
    enableTableRowSelection?: boolean;
    canReinviteSelectedRows?: (rows: Array<T>) => boolean;
    canDeleteSelectedRows?: (rows: Array<T>) => boolean;
    onRequestReinviteSelectedRows?: (
        rows: Array<T>,
    ) => Promise<boolean | void> | boolean | void;
    onRequestDeleteSelectedRows?: (rows: Array<T>) => Promise<void> | void;
    onRequestExportSelectedRows?: (rows: Array<T>) => void;
    onRequestRestoreSelectedRows?: (rows: Array<T>) => void;
    onRequestTransferSelectedRows?: (rows: Array<T>) => void;
    canTransferSelectedRows?: (rows: Array<T>) => boolean;
    canEditRow?: (row: T) => boolean;
    searchPlaceholder?: string;
    scopeFilterConfig?: ScopeFilterConfig;
    /** Event modules only: filters events by journey status column (`in` active or inactive UUID sets). */
    journeyScopeFilterConfig?: JourneyScopeFilterConfig;
    /** When true, the Display by control is visible but not interactive (e.g. Amaron sidebar status child views). */
    journeyScopeFilterDisabled?: boolean;
    /** Per-module rules for Clear (preserved mandatory filters, visibility, custom reset). */
    filterClearConfig?: DataViewerFilterClearConfig;
    /**
     * When false, the created-at date range pill is shown but no last-30-days range is auto-applied
     * (mount effect, persistence hydrate, or Clear). Omit or true for other modules. Default true.
     */
    defaultCreatedAtMonthFilter?: boolean;
    /** Portals/overlays for list-card footer flows (notes drawer, disposition modal, create sheet, …). */
    listCardChrome?: ReactNode;
    /** When the table loads zero rows successfully, used instead of the default no-search-results empty state. */
    renderTableNoDataEmptyState?: (props: {viewportBoundsElement: HTMLElement | null}) => ReactNode;
    /** Calendar customize-view metric options for the current module (workspace registry). */
    calendarDisplayMetrics?: CalendarDisplayMetricsConfig;
    /** Context for filtering visible metrics (e.g. sidebar status tab). */
    calendarDisplayMetricContext?: CalendarDisplayMetricVisibilityContext;
    calendarDisplayMetricId: string;
    setCalendarDisplayMetricId: (metricId: string) => void;
    activeCalendarDisplayMetric?: CalendarDisplayMetricOption;
    /** Resolved API column id for calendar visible-range filtering. */
    activeCalendarDateColumnId: string;
    resolveCalendarRowStartDate: (row: T) => string | undefined;
    /** Selects whether calendar reads use the controller's existing API or the orchestration capability. */
    calendarDataSource: CalendarDataSource;
    calendarOrchestrationEligibility: CalendarOrchestrationEligibility;

    // State
    currentViewId: Uuid | null;
    setCurrentViewId: Dispatch<SetStateAction<Uuid | null>>;
    setExplicitViewId: (id: Uuid) => void;
    searchConfiguration: SearchConfiguration;
    setSearchConfiguration: Dispatch<SetStateAction<SearchConfiguration>>;
    // paginationDetails: PaginationDetails;
    // setPaginationDetails: Dispatch<SetStateAction<PaginationDetails>>;
    sortConfiguration: SortConfiguration;
    setSortConfiguration: Dispatch<SetStateAction<SortConfiguration>>;
    /** Module default sort (e.g. createdAt desc); used when clearing sort back to the module default. */
    defaultSortConfiguration: SortConfiguration;
    /** When true (default), the sort trigger shows active state for the module default sort too. */
    showDefaultSortAsActive?: boolean;
    filterConfiguration: FilterConfiguration;
    setFilterConfiguration: Dispatch<SetStateAction<FilterConfiguration>>;
    visibleFilters: VisibleFiltersConfiguration;
    setVisibleFilters: Dispatch<SetStateAction<VisibleFiltersConfiguration>>;

    // Column configuration (user-driven overrides)
    tableColumnConfig: TableColumnConfig | null;
    setTableColumnConfig: Dispatch<SetStateAction<TableColumnConfig | null>>;
    kanbanColumnConfig: KanbanColumnConfig | null;
    setKanbanColumnConfig: Dispatch<SetStateAction<KanbanColumnConfig | null>>;

    // Queries
    rowCountQuery: UseQueryResult<number>;
    tableSelectedRowIds: Array<string>;
    setTableSelectedRowIds: Dispatch<SetStateAction<Array<string>>>;

    // TODO: Figure out how to break this into micro-stores or whatever
    currentlyOpenRowEngagementPanel: CurrentlyOpenRowEngagementPanel<T>;
    /** Open detail/profile panel, independent of checkbox selection for bulk actions. */
    activePanelRowId?: Uuid | null;
    setCurrentlyOpenRowEngagementPanel: Dispatch<SetStateAction<CurrentlyOpenRowEngagementPanel<T>>>;

    // Computed values
};

export function useOttopilotDataViewerController<T extends {id: Uuid}>({
    rowDisplayNameVariants,
    queryKeyPrefix,
    hideEditPanelFooter = false,
    hideReadPanelEditMenuItem = false,
    updatePanelOpensInFullscreen = false,
    mobileRowEngagementPanelInContentArea = false,
    forceListViewOnMobile = true,
    showEditActionPerRow = false,
    rowsQueryFn,
    calendarRowsQueryFn = rowsQueryFn,
    calendarYearDayCountsQueryFn = null,
    calendarRowColumns = null,
    rowCountQueryFn,
    kanbanColumnRowCountsQueryFn,
    kanbanAdvanceOffsetByPageSize,
    dateRangeFieldIds,
    dateRangeColumnId = DATA_VIEWER_CREATED_AT_DATE_RANGE_COLUMN_ID,
    columnValuesQueryFn,
    columns,
    views,
    initialViewId,
    initialSortConfiguration,
    showDefaultSortAsActive = true,
    initialFilterConfiguration,
    initialSearchConfiguration,
    searchColumns,
    initialVisibleFilters,
    breadCrumbItems,
    // TODO: Shift this to individual view configurations
    pageSize = 20,
    createPanelComponent,
    // deletePanelComponent,
    readPanelComponent,
    readPanelFooterComponent,
    resolveReadPanelHeaderLeft,
    updatePanelComponent,
    additionalActionComponent,
    tableRowActions,
    activePanelRowId,
    rowEngagementDropdownOptions,
    tableSelectionBulkAction,
    enableTableRowSelection = true,
    canReinviteSelectedRows,
    canDeleteSelectedRows,
    onRequestReinviteSelectedRows,
    onRequestDeleteSelectedRows,
    onRequestExportSelectedRows,
    onRequestRestoreSelectedRows,
    onRequestTransferSelectedRows,
    canTransferSelectedRows,
    canEditRow,
    searchPlaceholder,
    scopeFilterConfig,
    journeyScopeFilterConfig,
    journeyScopeFilterDisabled,
    filterClearConfig,
    listCardChrome,
    renderTableNoDataEmptyState,
    defaultCreatedAtMonthFilter,
    persistFilters = true,
    persistSort = true,
    viewPrefsConfigSignature,
    applyInitialFilterOverlay,
    isModuleStatusChildView = false,
    moduleStatusManagedColumnIds,
    calendarDisplayMetrics,
    calendarDisplayMetricContext,
    calendarDataSource = "direct",
    calendarOrchestrationEligibility = "absent",
    mapCalendarDisplayMetricFilterColumnId,
}: {
    rowDisplayNameVariants: {
        singularSentenceCase: string;
        pluralSentenceCase: string;
        singularLowerCase: string;
        pluralLowerCase: string;
    };
    queryKeyPrefix: Array<string | null>;
    /** For profiles/forms hosted outside the data viewer's own row panel. */
    activePanelRowId?: Uuid | null;
    hideEditPanelFooter?: boolean;
    hideReadPanelEditMenuItem?: boolean;
    updatePanelOpensInFullscreen?: boolean;
    mobileRowEngagementPanelInContentArea?: boolean;
    /**
     * When true (default), mobile viewports default to list view when a list view exists.
     * Users can still switch views via the view picker. Set false for My Day Plan / Home
     * so Focus / Kanban / explicit deep links can be the initial mobile view.
     */
    forceListViewOnMobile?: boolean;
    showEditActionPerRow?: boolean;
    rowsQueryFn: OttopilotRowsQueryFn<T>;
    calendarRowsQueryFn?: OttopilotRowsQueryFn<T>;
    calendarYearDayCountsQueryFn?: ((props: {
        filterConfiguration: FilterConfiguration;
        searchConfiguration: SearchConfiguration;
        dateColumnId: string;
    }) => Promise<Array<{count: number; value: string | null}>>) | null;
    calendarRowColumns?: Array<string> | null;
    rowCountQueryFn: (props: {
        filterConfiguration: FilterConfiguration;
        searchConfiguration: SearchConfiguration;
    }) => Promise<Integer>; // symbol |
    kanbanColumnRowCountsQueryFn?: (props: {
        filterConfiguration: FilterConfiguration;
        sortConfiguration: SortConfiguration;
        searchConfiguration: SearchConfiguration;
    }) => Promise<Record<string, number>>;
    kanbanAdvanceOffsetByPageSize?: boolean;
    dateRangeFieldIds?: {
        startDateColumnId?: string;
        endDateColumnId?: string;
    };
    dateRangeColumnId?: string;
    columnValuesQueryFn: (props: {
        columnId: string;
        filterConfiguration?: FilterConfiguration;
        searchConfiguration?: SearchConfiguration;
    }) => Promise<Array<string>>;
    columns: {
        revisionId: Uuid;
        lineItems: Array<OttopilotColumn<T>>;
    };
    views: Array<OttopilotView<T>>;
    initialViewId?: Uuid;
    searchColumns?: Array<string>;
    initialSortConfiguration?: SortConfiguration;
    showDefaultSortAsActive?: boolean;
    initialSearchConfiguration?: SearchConfiguration;
    initialFilterConfiguration?: FilterConfiguration;
    initialVisibleFilters?: {
        revisionId: Uuid;
        // lineItems: Array<Uuid>;
        lineItems: Array<string>;
    };
    pageSize?: number;
    // deletePanelComponent: (row: T) => React.ReactNode;
    createPanelComponent?: (isExpanded: boolean) => React.ReactNode;
    readPanelComponent?: (row: T, isExpanded: boolean) => React.ReactNode;
    /** Optional footer for the read (view) panel; replaces the default Edit footer button. */
    readPanelFooterComponent?: (row: T) => React.ReactNode;
    /** Optional title for the read panel header; defaults to "View {singularLowerCase}". */
    resolveReadPanelHeaderLeft?: (props: {row: T; readPanelLayout?: ReadPanelLayout}) => string | undefined;
    updatePanelComponent?: (row: T, isExpanded: boolean) => React.ReactNode;
    additionalActionComponent?: (row: T) => React.ReactNode;
    tableRowActions?: TableRowActionsConfig<T>;
    rowEngagementDropdownOptions?: (row: T) => Array<React.ReactNode>;
    tableSelectionBulkAction?: "delete" | "none" | "reinvite" | "restore";
    enableTableRowSelection?: boolean;
    canReinviteSelectedRows?: (rows: Array<T>) => boolean;
    canDeleteSelectedRows?: (rows: Array<T>) => boolean;
    onRequestReinviteSelectedRows?: (
        rows: Array<T>,
    ) => Promise<boolean | void> | boolean | void;
    onRequestDeleteSelectedRows?: (rows: Array<T>) => Promise<void> | void;
    onRequestExportSelectedRows?: (rows: Array<T>) => void;
    onRequestRestoreSelectedRows?: (rows: Array<T>) => void;
    onRequestTransferSelectedRows?: (rows: Array<T>) => void;
    canTransferSelectedRows?: (rows: Array<T>) => boolean;
    canEditRow?: (row: T) => boolean;
    searchPlaceholder?: string;
    scopeFilterConfig?: ScopeFilterConfig;
    journeyScopeFilterConfig?: JourneyScopeFilterConfig;
    journeyScopeFilterDisabled?: boolean;
    filterClearConfig?: DataViewerFilterClearConfig;
    listCardChrome?: ReactNode;
    renderTableNoDataEmptyState?: (props: {viewportBoundsElement: HTMLElement | null}) => ReactNode;
    defaultCreatedAtMonthFilter?: boolean;
    /** When true (default), filter/search/sort/visible-filters state is restored from localStorage per module. */
    persistFilters?: boolean;
    /** When false, sort always uses `initialSortConfiguration` and is not saved to localStorage. */
    persistSort?: boolean;
    /**
     * When set, config-driven pages persist filters/sort/columns in the v2 blob keyed by this
     * signature. Mismatch with stored blob clears localStorage for that page.
     */
    viewPrefsConfigSignature?: string;
    /**
     * Runs after persisted filters are merged with `initialFilterConfiguration`.
     * Use for sidebar `statusId` (and similar) so module-status filters are not dropped by persistence.
     */
    applyInitialFilterOverlay?: (filterConfiguration: FilterConfiguration) => FilterConfiguration;
    /**
     * When true, sidebar status nav owns the status column — journey scope is not restored from blob
     * and Display by should be disabled.
     */
    isModuleStatusChildView?: boolean;
    /** Status column ids managed by sidebar nav; stripped from blob save/load. */
    moduleStatusManagedColumnIds?: ReadonlyArray<string>;
    calendarDisplayMetrics?: CalendarDisplayMetricsConfig;
    calendarDisplayMetricContext?: CalendarDisplayMetricVisibilityContext;
    calendarDataSource?: CalendarDataSource;
    calendarOrchestrationEligibility?: CalendarOrchestrationEligibility;
    mapCalendarDisplayMetricFilterColumnId?: (metric: CalendarDisplayMetricOption) => string;
    breadCrumbItems?: {
        displayName: string;
        to: string | null;
    }[];
}): OttopilotDataViewerController<T> {
    const showActionsColumn =
        readPanelComponent !== undefined ||
        updatePanelComponent !== undefined ||
        additionalActionComponent !== undefined ||
        tableRowActions !== undefined;

    const augmentedColumns = useMemo(() => {
        if (!showActionsColumn) {
            return columns;
        }

        const hasActionsColumn = columns.lineItems.some((col) => col.id === "__actions__");
        if (hasActionsColumn) {
            return columns;
        }

        const actionsColumn: OttopilotColumn<T> = {
            id: "__actions__",
            displayName: "Actions",
            type: OttopilotColumnType.string,
            valueGetter: () => "",
            pinned: "right",
        };

        return {
            ...columns,
            lineItems: [...columns.lineItems, actionsColumn],
        };
    }, [columns, showActionsColumn]);

    const augmentedColumnsWithDateRange = useMemo(() => {
        const columnId = DATA_VIEWER_CREATED_AT_DATE_RANGE_COLUMN_ID;
        if (augmentedColumns.lineItems.some((column) => column.id === columnId)) {
            return augmentedColumns;
        }

        const createdAtColumn: OttopilotColumn<T> = {
            id: columnId,
            displayName: "Created at",
            type: OttopilotColumnType.iso8601DateTime,
            valueGetter: (row) =>
                (row as {createdAt?: Iso8601DateTime}).createdAt ?? ("" as Iso8601DateTime),
            valueFormatter: (value) => formatDateTime(value),
            filteringCapability_contains: false,
        };

        return {
            ...augmentedColumns,
            lineItems: [createdAtColumn, ...augmentedColumns.lineItems],
        };
    }, [augmentedColumns]);

    const augmentedViews = useMemo(() => {
        if (!showActionsColumn) {
            return views;
        }

        return views.map((v) => {
            if (v.viewType === ViewType.table && !v.visibleColumns.includes("__actions__")) {
                return {
                    ...v,
                    visibleColumns: [...v.visibleColumns, "__actions__"],
                };
            }
            return v;
        });
    }, [views, showActionsColumn]);

    const isMobileViewport = useMediaQuery(MOBILE_VIEWPORT_MEDIA_QUERY, false, {
        getInitialValueInEffect: true,
    });

    // On mobile, prefer list when configured (default). Day plan / home opt out via
    // `forceListViewOnMobile: false` so Focus / Kanban / explicit deep links can be initial.
    const mobilePreferredViewId = useMemo(() => {
        if (isMobileViewport !== true) {
            return null;
        }

        const listView = augmentedViews.find((view) => view.viewType === ViewType.list);

        if (forceListViewOnMobile === true) {
            if (listView !== undefined) {
                return listView.id;
            }
            if (initialViewId !== undefined) {
                const initialView = augmentedViews.find((view) => view.id === initialViewId);
                if (initialView !== undefined) {
                    return initialView.id;
                }
            }
            return null;
        }

        if (initialViewId !== undefined) {
            const initialView = augmentedViews.find((view) => view.id === initialViewId);
            if (initialView !== undefined) {
                return initialView.id;
            }
        }
        if (listView !== undefined) {
            return listView.id;
        }
        const kanbanView = augmentedViews.find((view) => view.viewType === ViewType.kanbanBoard);
        return kanbanView?.id ?? null;
    }, [isMobileViewport, augmentedViews, initialViewId, forceListViewOnMobile]);

    const desktopPreferredViewId = useMemo(() => {
        if (initialViewId !== undefined) {
            return initialViewId;
        }
        const tableView = augmentedViews.find((v) => v.viewType === ViewType.table);
        if (tableView !== undefined) {
            return tableView.id;
        }
        if (augmentedViews.length > 0) {
            const ordered = sortOttopilotViewsForDisplayOrder(augmentedViews);
            return ordered[0]!.id;
        }
        return null;
    }, [initialViewId, augmentedViews]);

    // Track whether the mobile default override is still active (not yet dismissed by user)
    const mobilePreferredViewActiveRef = useRef(false);
    // When forcing list on mobile, ignore non-list initialViewId so hydration can switch to list.
    // Day plan / home keep intentional initial views (e.g. Focus deep links).
    const userDismissedMobileDefaultRef = useRef(
        forceListViewOnMobile === true
            ? initialViewId !== undefined
                && augmentedViews.find((view) => view.id === initialViewId)?.viewType === ViewType.list
            : initialViewId !== undefined,
    );
    const previousIsMobileViewportRef = useRef<boolean | null>(null);

    const [currentViewId, setCurrentViewIdRaw] = useState<Uuid | null>(() => {
        if (initialViewId !== undefined) {
            return initialViewId;
        }

        if (augmentedViews.length > 0) {
            const ordered = sortOttopilotViewsForDisplayOrder(augmentedViews);
            return ordered[0]!.id;
        }

        return null;
    });

    useEffect(() => {
        const isMobile = isMobileViewport === true;
        const wasMobile = previousIsMobileViewportRef.current;

        if (wasMobile === null) {
            previousIsMobileViewportRef.current = isMobile;
            // Media-query hydration often starts as desktop then flips to mobile. If an
            // explicit view was already chosen (initialViewId / setExplicitViewId), keep it.
            if (isMobile === true && userDismissedMobileDefaultRef.current === true) {
                mobilePreferredViewActiveRef.current = false;
                return;
            }
            if (isMobile === true && mobilePreferredViewId !== null) {
                mobilePreferredViewActiveRef.current = true;
                setCurrentViewIdRaw(mobilePreferredViewId);
            }
            return;
        }

        if (wasMobile === isMobile) {
            return;
        }

        previousIsMobileViewportRef.current = isMobile;

        if (isMobile) {
            if (userDismissedMobileDefaultRef.current === true) {
                mobilePreferredViewActiveRef.current = false;
                return;
            }
            if (mobilePreferredViewId !== null) {
                mobilePreferredViewActiveRef.current = true;
                setCurrentViewIdRaw(mobilePreferredViewId);
            } else {
                mobilePreferredViewActiveRef.current = false;
            }
            return;
        }

        mobilePreferredViewActiveRef.current = false;
        userDismissedMobileDefaultRef.current = false;
        if (desktopPreferredViewId !== null) {
            setCurrentViewIdRaw(desktopPreferredViewId);
        }
    }, [isMobileViewport, mobilePreferredViewId, desktopPreferredViewId]);

    // Wrap setCurrentViewId: on mobile, intercept programmatic overrides that try to
    // switch away from the mobile default. Once the user manually picks a different view
    // (via the ViewPicker), the ref is cleared and subsequent calls pass through normally.
    const setCurrentViewId: Dispatch<SetStateAction<Uuid | null>> = useCallback(
        (action) => {
            setCurrentViewIdRaw((prev) => {
                const next = typeof action === "function" ? action(prev) : action;
                if (
                    mobilePreferredViewActiveRef.current &&
                    mobilePreferredViewId !== null &&
                    next !== mobilePreferredViewId
                ) {
                    return mobilePreferredViewId;
                }
                return next;
            });
        },
        [mobilePreferredViewId],
    );
    const currentView = currentViewId === null
        ? null
        : augmentedViews.find((view) => view.id === currentViewId);
    const shouldFetchRowCount =
        currentView?.viewType !== ViewType.kanbanBoard && currentView?.viewType !== ViewType.calendar;
    // const [paginationDetails, setPaginationDetails] = useState<PaginationDetails>(() => ({
    //     revisionId: generateUuid(),
    //     pageNumber: 1,
    //     pageSize: 10,
    // }));

    const filterPersistenceStorageKey = useMemo(
        () => buildDataViewerFilterPersistenceStorageKey(queryKeyPrefix),
        [queryKeyPrefix],
    );
    const configPageViewPrefsStorageKey = useMemo(
        () => buildConfigPageViewPreferencesStorageKey(queryKeyPrefix),
        [queryKeyPrefix],
    );
    const useConfigPageViewPreferences = viewPrefsConfigSignature !== undefined;
    const shouldPersistFilterState = persistFilters === true;

    const initialConfigPageViewPrefs = ((): ConfigPageViewPreferencesV2 | null => {
        if (useConfigPageViewPreferences !== true || shouldPersistFilterState !== true) {
            return null;
        }
        return loadConfigPageViewPreferences(configPageViewPrefsStorageKey, viewPrefsConfigSignature);
    })();

    const initialFilterPersistence = ((): DataViewerFilterPersistenceV1 | null => {
        if (shouldPersistFilterState !== true) {
            return null;
        }
        if (initialConfigPageViewPrefs !== null) {
            return {
                version: 1,
                filterLineItems: initialConfigPageViewPrefs.filterLineItems,
                visibleFilterColumnIds: initialConfigPageViewPrefs.visibleFilterColumnIds,
                searchValue: initialConfigPageViewPrefs.searchValue,
                sortLineItems: initialConfigPageViewPrefs.sortLineItems,
            };
        }
        return loadDataViewerFilterPersistence(filterPersistenceStorageKey);
    })();

    useEffect(() => {
        if (shouldPersistFilterState === true) {
            return;
        }
        clearDataViewerFilterPersistence(filterPersistenceStorageKey);
        if (useConfigPageViewPreferences === true) {
            clearConfigPageViewPreferences(configPageViewPrefsStorageKey);
        }
    }, [
        configPageViewPrefsStorageKey,
        filterPersistenceStorageKey,
        shouldPersistFilterState,
        useConfigPageViewPreferences,
    ]);

    const [defaultSortConfiguration] = useState<SortConfiguration>(() =>
        resolveDataViewerDefaultSortConfiguration(initialSortConfiguration),
    );
    const [sortConfiguration, setSortConfiguration] = useState<SortConfiguration>(() => {
        const resolved = resolveSortConfigurationFromPersistence({
            persisted: initialFilterPersistence,
            initialSortConfiguration,
            ignorePersistedSort: persistSort === false,
        });
        return resolved ?? resolveDataViewerDefaultSortConfiguration(initialSortConfiguration);
    });
    const [searchConfiguration, setSearchConfiguration] = useState<SearchConfiguration>(() => {
        return resolveSearchConfigurationFromPersistence({
            persisted: initialFilterPersistence,
            initialSearchConfiguration,
            searchColumns,
        });
    });
    const [filterConfiguration, setFilterConfiguration] = useState<FilterConfiguration>(() => {
        const resolved = resolveFilterConfigurationFromPersistence({
            dateRangeColumnId: dateRangeColumnId,
            persisted: initialFilterPersistence,
            initialFilterConfiguration,
            filterClearConfig,
            defaultCreatedAtMonthFilter: defaultCreatedAtMonthFilter !== false,
            journeyScopeFilterConfig: journeyScopeFilterConfig,
            persistedJourneyScopeSelection: initialConfigPageViewPrefs?.journeyScopeSelection,
            persistedCreatedAtDateRange: initialConfigPageViewPrefs?.createdAtDateRange,
            moduleStatusManagedColumnIds: moduleStatusManagedColumnIds,
            skipJourneyScopeReapply: isModuleStatusChildView === true,
        });
        if (applyInitialFilterOverlay !== undefined) {
            return applyInitialFilterOverlay(resolved);
        }
        return resolved;
    });
    const [visibleFilters, setVisibleFilters] = useState<VisibleFiltersConfiguration>(() => {
        return resolveVisibleFiltersFromPersistence({
            dateRangeColumnId: dateRangeColumnId,
            persisted: initialFilterPersistence,
            initialVisibleFilters,
        });
    });

    const [tableColumnConfig, setTableColumnConfig] = useState<TableColumnConfig | null>(() => {
        return toTableColumnConfigFromPreferences(initialConfigPageViewPrefs?.tableColumnConfig ?? null);
    });
    const [kanbanColumnConfig, setKanbanColumnConfig] = useState<KanbanColumnConfig | null>(() => {
        return toKanbanColumnConfigFromPreferences(initialConfigPageViewPrefs?.kanbanColumnConfig ?? null);
    });

    const calendarDisplayMetricContextResolved = calendarDisplayMetricContext ?? {};
    const calendarMetricPersistenceStorageKey = useMemo(
        () => buildCalendarDisplayMetricPersistenceStorageKey(queryKeyPrefix),
        [queryKeyPrefix],
    );
    const calendarDisplayMetricStatusId = calendarDisplayMetricContextResolved.statusId ?? null;

    const [calendarDisplayMetricId, setCalendarDisplayMetricIdState] = useState(() => {
        if (calendarDisplayMetrics === undefined) {
            return "createdOn";
        }
        const persisted = loadCalendarDisplayMetricPersistence(calendarMetricPersistenceStorageKey);
        if (
            persisted !== null &&
            findCalendarDisplayMetricOption(calendarDisplayMetrics, persisted, calendarDisplayMetricContextResolved) !==
                undefined
        ) {
            return persisted;
        }
        return resolveDefaultCalendarDisplayMetricId(calendarDisplayMetrics, calendarDisplayMetricContextResolved);
    });

    useEffect(() => {
        if (calendarDisplayMetrics === undefined) {
            return;
        }
        const ctx = {statusId: calendarDisplayMetricStatusId};
        const valid = findCalendarDisplayMetricOption(calendarDisplayMetrics, calendarDisplayMetricId, ctx);
        if (valid === undefined) {
            const next = resolveDefaultCalendarDisplayMetricId(calendarDisplayMetrics, ctx);
            setCalendarDisplayMetricIdState(next);
            saveCalendarDisplayMetricPersistence(calendarMetricPersistenceStorageKey, next);
        }
    }, [
        calendarDisplayMetricId,
        calendarDisplayMetricStatusId,
        calendarDisplayMetrics,
        calendarMetricPersistenceStorageKey,
    ]);

    const setCalendarDisplayMetricId = useCallback(
        (metricId: string) => {
            setCalendarDisplayMetricIdState(metricId);
            saveCalendarDisplayMetricPersistence(calendarMetricPersistenceStorageKey, metricId);
        },
        [calendarMetricPersistenceStorageKey],
    );

    const activeCalendarDisplayMetric = useMemo(() => {
        if (calendarDisplayMetrics === undefined) {
            return undefined;
        }
        return findCalendarDisplayMetricOption(calendarDisplayMetrics, calendarDisplayMetricId, {
            statusId: calendarDisplayMetricStatusId,
        });
    }, [calendarDisplayMetricId, calendarDisplayMetricStatusId, calendarDisplayMetrics]);

    const activeCalendarDateColumnId = useMemo(() => {
        if (activeCalendarDisplayMetric !== undefined) {
            return mapCalendarDisplayMetricFilterColumnId?.(activeCalendarDisplayMetric) ??
                resolveCalendarDisplayMetricFilterColumnId(activeCalendarDisplayMetric.columnRef);
        }
        return dateRangeFieldIds?.startDateColumnId ?? "createdAt";
    }, [activeCalendarDisplayMetric, dateRangeFieldIds?.startDateColumnId, mapCalendarDisplayMetricFilterColumnId]);

    const resolveCalendarRowStartDate = useCallback(
        (row: T): string | undefined => {
            const createdAtIso = resolveCalendarMetricIsoStart(row, {kind: "system", columnId: "createdAt"});
            if (activeCalendarDisplayMetric !== undefined) {
                return resolveCalendarMetricIsoStart(row, activeCalendarDisplayMetric.columnRef);
            }
            const rowWithDates = row as T & {startDate?: string};
            if (
                rowWithDates.startDate !== undefined &&
                rowWithDates.startDate.trim() !== "" &&
                Number.isNaN(Date.parse(rowWithDates.startDate)) === false
            ) {
                return rowWithDates.startDate;
            }
            const fallbackCol = dateRangeFieldIds?.startDateColumnId ?? "createdAt";
            if (fallbackCol === "createdAt" || fallbackCol === "updatedAt") {
                return resolveCalendarMetricIsoStart(row, {kind: "system", columnId: fallbackCol}) ?? createdAtIso;
            }
            return (
                resolveCalendarMetricIsoStart(row, {
                    kind: "eventAdditionalColumn",
                    columnId: fallbackCol as Uuid,
                }) ?? createdAtIso
            );
        },
        [activeCalendarDisplayMetric, dateRangeFieldIds?.startDateColumnId],
    );

    const previousViewIdForCalendarDateFilterRef = useRef<Uuid | null>(null);

    useEffect(() => {
        const previousViewId = previousViewIdForCalendarDateFilterRef.current;
        const previousView =
            previousViewId === null ? undefined : augmentedViews.find((view) => view.id === previousViewId);
        const previousViewType = previousView?.viewType;
        const nextViewType = currentView?.viewType;

        if (nextViewType === ViewType.calendar) {
            if (previousViewType !== ViewType.calendar || previousViewId === null) {
                setFilterConfiguration((previousFilterConfiguration) =>
                    stripDateRangeFilterLineItemsFromFilterConfiguration(previousFilterConfiguration, [
                        dateRangeColumnId,
                    ]),
                );
            }
        } else if (previousViewType === ViewType.calendar) {
            if (defaultCreatedAtMonthFilter !== false) {
                setFilterConfiguration((previousFilterConfiguration) =>
                    mergeDefaultCreatedAtMonthFilter(
                        previousFilterConfiguration,
                        dateRangeColumnId,
                        true,
                    ),
                );
            }
        }

        previousViewIdForCalendarDateFilterRef.current = currentViewId;
    }, [augmentedViews, currentView, currentViewId, dateRangeColumnId, defaultCreatedAtMonthFilter]);

    useEffect(() => {
        if (shouldPersistFilterState === false) {
            return;
        }
        const timeoutId = window.setTimeout(() => {
            const persistedFilterLineItems = stripCreatedAtDateRangeLineItems(
                stripManagedFilterLineItemsForPersistence(filterConfiguration.lineItems, {
                    journeyScopeFilterConfig: journeyScopeFilterConfig,
                    moduleStatusManagedColumnIds: moduleStatusManagedColumnIds,
                }),
                dateRangeColumnId,
            );
            const [createdAtFrom, createdAtTo] = readCreatedAtDateRangeFromFilters(
                filterConfiguration.lineItems,
                dateRangeColumnId,
            );
            const createdAtDateRange =
                createdAtFrom !== null && createdAtTo !== null
                    ? {from: createdAtFrom, to: createdAtTo}
                    : null;
            if (viewPrefsConfigSignature !== undefined) {
                saveConfigPageViewPreferences(
                    configPageViewPrefsStorageKey,
                    buildConfigPageViewPreferencesFromState({
                        configSignature: viewPrefsConfigSignature,
                        filterLineItems: persistedFilterLineItems,
                        visibleFilterColumnIds: visibleFilters.lineItems,
                        searchValue: searchConfiguration.searchValue,
                        sortLineItems: persistSort === false ? [] : sortConfiguration.lineItems,
                        tableColumnConfig: tableColumnConfig,
                        kanbanColumnConfig: kanbanColumnConfig,
                        journeyScopeSelection:
                            isModuleStatusChildView === true || journeyScopeFilterConfig === undefined
                                ? undefined
                                : getJourneyScopeSelectionFromFilter(
                                      filterConfiguration,
                                      journeyScopeFilterConfig,
                                  ),
                        createdAtDateRange: createdAtDateRange,
                    }),
                    dateRangeColumnId,
                );
                return;
            }
            saveDataViewerFilterPersistence(filterPersistenceStorageKey, {
                version: 1,
                filterLineItems: persistedFilterLineItems,
                visibleFilterColumnIds: visibleFilters.lineItems,
                searchValue: searchConfiguration.searchValue,
                sortLineItems: persistSort === false ? [] : sortConfiguration.lineItems,
            }, dateRangeColumnId);
        }, 300);
        return () => {
            window.clearTimeout(timeoutId);
        };
    }, [
        configPageViewPrefsStorageKey,
        dateRangeColumnId,
        filterConfiguration.lineItems,
        filterPersistenceStorageKey,
        isModuleStatusChildView,
        journeyScopeFilterConfig,
        kanbanColumnConfig,
        moduleStatusManagedColumnIds,
        searchConfiguration.searchValue,
        shouldPersistFilterState,
        sortConfiguration.lineItems,
        tableColumnConfig,
        useConfigPageViewPreferences,
        viewPrefsConfigSignature,
        visibleFilters.lineItems,
        persistSort,
    ]);

    const [tableSelectedRowIds, setTableSelectedRowIds] = useState<string[]>([]);
    const [pageSizeState, setPageSize] = useState(() => (pageSize > 0 ? pageSize : 50));
    const [pageNumber, setPageNumber] = useState(1);

    useEffect(() => {
        setTableSelectedRowIds([]);
        setPageNumber(1);
    }, [
        currentViewId,
        filterConfiguration.revisionId,
        searchConfiguration.revisionId,
        sortConfiguration.revisionId,
        pageSizeState,
    ]);

    // const queryClient = useQueryClient();

    const rowCountQuery = useQuery<number>({
        queryKey: [
            "ottopilotDataViewer",
            ...queryKeyPrefix,
            "rowCount",
            filterConfiguration.revisionId,
            searchConfiguration.revisionId,
        ],
        queryFn: () =>
            rowCountQueryFn({
                filterConfiguration: filterConfiguration,
                searchConfiguration: searchConfiguration,
            }),
        retry: false,
        staleTime: Infinity,
        refetchOnReconnect: false,
        refetchOnWindowFocus: false,
        enabled: shouldFetchRowCount,
    });

    const [currentlyOpenRowEngagementPanel, setCurrentlyOpenRowEngagementPanel] = useState<
        CurrentlyOpenRowEngagementPanel<T>
    >({
        rowEngagementType: RowEngagementType.none,
        rowEngagementPanelType: RowEngagementPanelType.none,
        rowEngagementData: null,
    });

    // const invalidateRowsQuery = () => {
    //     // // Reset things on next frame, because we mostly do a setState right before calling this function,
    //     // // which leads to stale state being picked up.
    //     // setTimeout(() => {
    //     //     queryClient.invalidateQueries({
    //     //         queryKey: ["ottopilotDataViewer", ...queryKeyPrefix, "rows"],
    //     //     });
    //     // }, 0);

    //     queryClient.invalidateQueries({
    //         queryKey: ["ottopilotDataViewer", ...queryKeyPrefix, "rows"],
    //     });
    // };

    const wrappedColumnValuesQueryFn = async (props: {
        columnId: string;
        filterConfiguration?: FilterConfiguration;
        searchConfiguration?: SearchConfiguration;
    }): Promise<Array<string>> => {
        const values = await columnValuesQueryFn(props);
        return sanitizeCreatedUpdatedByFilterColumnValues(props.columnId, values);
    };

    return {
        breadCrumbItems: breadCrumbItems,
        rowDisplayNameVariants: rowDisplayNameVariants,
        columns: augmentedColumnsWithDateRange,
        views: augmentedViews,
        queryKeyPrefix: queryKeyPrefix,
        rowsQueryFn: rowsQueryFn,
        calendarRowsQueryFn: calendarRowsQueryFn,
        calendarYearDayCountsQueryFn: calendarYearDayCountsQueryFn,
        calendarRowColumns: calendarRowColumns,
        rowCountQueryFn: rowCountQueryFn,
        kanbanColumnRowCountsQueryFn: kanbanColumnRowCountsQueryFn,
        kanbanAdvanceOffsetByPageSize: kanbanAdvanceOffsetByPageSize,
        dateRangeColumnId: dateRangeColumnId,
        dateRangeFieldIds: {
            startDateColumnId: dateRangeFieldIds?.startDateColumnId ?? "createdAt",
            endDateColumnId: dateRangeFieldIds?.endDateColumnId ?? "createdAt",
        },
        pageSize: pageSizeState,
        setPageSize: setPageSize,
        pageNumber: pageNumber,
        setPageNumber: setPageNumber,
        columnValuesQueryFn: wrappedColumnValuesQueryFn,
        hideEditPanelFooter: hideEditPanelFooter,
        hideReadPanelEditMenuItem: hideReadPanelEditMenuItem,
        updatePanelOpensInFullscreen: updatePanelOpensInFullscreen,
        mobileRowEngagementPanelInContentArea: mobileRowEngagementPanelInContentArea,
        forceListViewOnMobile: forceListViewOnMobile,
        showEditActionPerRow: showEditActionPerRow,
        createPanelComponent: createPanelComponent,
        // deletePanelComponent: deletePanelComponent,
        readPanelComponent: readPanelComponent,
        readPanelFooterComponent: readPanelFooterComponent,
        resolveReadPanelHeaderLeft: resolveReadPanelHeaderLeft,
        updatePanelComponent: updatePanelComponent,
        additionalActionComponent: additionalActionComponent,
        tableRowActions: tableRowActions,
        rowEngagementDropdownOptions: rowEngagementDropdownOptions,
        tableSelectionBulkAction: tableSelectionBulkAction,
        enableTableRowSelection: enableTableRowSelection,
        canReinviteSelectedRows: canReinviteSelectedRows,
        canDeleteSelectedRows: canDeleteSelectedRows,
        onRequestReinviteSelectedRows: onRequestReinviteSelectedRows,
        onRequestDeleteSelectedRows: onRequestDeleteSelectedRows,
        onRequestExportSelectedRows: onRequestExportSelectedRows,
        onRequestRestoreSelectedRows: onRequestRestoreSelectedRows,
        onRequestTransferSelectedRows: onRequestTransferSelectedRows,
        canTransferSelectedRows: canTransferSelectedRows,
        canEditRow: canEditRow,
        searchPlaceholder: searchPlaceholder,
        scopeFilterConfig: scopeFilterConfig,
        journeyScopeFilterConfig: journeyScopeFilterConfig,
        journeyScopeFilterDisabled: journeyScopeFilterDisabled,
        filterClearConfig: filterClearConfig,
        defaultCreatedAtMonthFilter: defaultCreatedAtMonthFilter,
        listCardChrome: listCardChrome,
        renderTableNoDataEmptyState: renderTableNoDataEmptyState,
        calendarDisplayMetrics: calendarDisplayMetrics,
        calendarDisplayMetricContext: calendarDisplayMetricContext,
        calendarDisplayMetricId: calendarDisplayMetricId,
        setCalendarDisplayMetricId: setCalendarDisplayMetricId,
        activeCalendarDisplayMetric: activeCalendarDisplayMetric,
        activeCalendarDateColumnId: activeCalendarDateColumnId,
        resolveCalendarRowStartDate: resolveCalendarRowStartDate,
        calendarDataSource: calendarDataSource,
        calendarOrchestrationEligibility: calendarOrchestrationEligibility,
        searchConfiguration,
        setSearchConfiguration,
        currentViewId: currentViewId,
        setCurrentViewId: setCurrentViewId,
        setExplicitViewId: (id: Uuid) => {
            userDismissedMobileDefaultRef.current = true;
            mobilePreferredViewActiveRef.current = false;
            setCurrentViewIdRaw(id);
        },
        sortConfiguration: sortConfiguration,
        setSortConfiguration: setSortConfiguration,
        defaultSortConfiguration: defaultSortConfiguration,
        showDefaultSortAsActive: showDefaultSortAsActive,
        filterConfiguration: filterConfiguration,
        setFilterConfiguration: setFilterConfiguration,
        visibleFilters: visibleFilters,
        setVisibleFilters: setVisibleFilters,
        tableColumnConfig: tableColumnConfig,
        setTableColumnConfig: setTableColumnConfig,
        kanbanColumnConfig: kanbanColumnConfig,
        setKanbanColumnConfig: setKanbanColumnConfig,
        rowCountQuery: rowCountQuery,
        tableSelectedRowIds: tableSelectedRowIds,
        setTableSelectedRowIds: setTableSelectedRowIds,
        // invalidateRowsQuery: invalidateRowsQuery,
        currentlyOpenRowEngagementPanel: currentlyOpenRowEngagementPanel,
        activePanelRowId: activePanelRowId ?? (
            currentlyOpenRowEngagementPanel.rowEngagementType !== RowEngagementType.none
                ? currentlyOpenRowEngagementPanel.rowEngagementData?.id ?? null
                : null
        ),
        setCurrentlyOpenRowEngagementPanel: setCurrentlyOpenRowEngagementPanel,
    };
}
